import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Globe, LogIn, Monitor } from 'lucide-react';

const DESKTOP_BREAKPOINT = 1024;

const DesktopGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [isDesktop, setIsDesktop] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isDesktop || dismissed) {
    return <>{children}</>;
  }

  const currentUrl = window.location.href;

  return (
    <div className="fixed inset-0 z-[99999] bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-8">
      <div className="max-w-lg w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <img
            src="/icons/logo-512x512.png"
            alt="Nonna Vittoria Apartments"
            className="w-20 h-20 rounded-2xl mx-auto shadow-lg"
          />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Nonna Vittoria Apartments
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
          {t('desktopGate.subtitle', 'Questa app è progettata per smartphone e tablet')}
        </p>

        {/* QR Code */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 inline-block mb-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Smartphone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('desktopGate.scanQR', 'Scansiona per aprire sul telefono')}
            </span>
          </div>
          <div className="bg-white p-3 rounded-xl inline-block">
            <QRCodeSVG
              value={currentUrl}
              size={180}
              level="M"
              includeMargin={false}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6 max-w-xs mx-auto">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wider">
            {t('desktopGate.or', 'oppure')}
          </span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 max-w-xs mx-auto mb-8">
          <a
            href="https://nonnavittoriaapartments.it"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-5 rounded-xl transition-all shadow-sm"
          >
            <Globe className="h-4 w-4" />
            {t('desktopGate.officialSite', 'Vai al Sito Ufficiale')}
          </a>
          <a
            href="https://book.nonnavittoriaapartments.it/checkin"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold py-3 px-5 rounded-xl transition-all border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            <LogIn className="h-4 w-4" />
            {t('desktopGate.onlineCheckIn', 'Check-in Online')}
          </a>
        </div>

        {/* Continue anyway */}
        <button
          onClick={() => setDismissed(true)}
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <Monitor className="h-3.5 w-3.5" />
          {t('desktopGate.continueDesktop', 'Continua comunque su desktop')}
        </button>
      </div>
    </div>
  );
};

export default DesktopGate;
